"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";

export default function MenuBoardsBrandingScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-[#FAFAF8] overflow-hidden">
      <div className="max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#D7263D" }}>
            {t("Your branding", "Tu marca")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02] text-redy-charcoal">
            {t("Yours. ", "Tuya. ")}
            <span className="text-redy-charcoal/30">{t("Not theirs.", "No suya.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-redy-secondary leading-relaxed mt-6">
            {t(
              "Generic menu-board templates all look the same. Yours shouldn't. Upload your logo, pick your colors, drop a hero photo. The screen looks like your restaurant — not like a software template.",
              "Los templates genéricos de menu board se ven todos iguales. El tuyo no debería. Sube tu logo, elige tus colores, suelta una foto hero. La pantalla se ve como tu restaurante — no como un template de software."
            )}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-[960px] mx-auto">
          {/* Generic template — no real vendor branding */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.21, 0.47, 0.32, 0.98] }}
          >
            <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/55 mb-4">
              {t("Generic template", "Template genérico")}
            </div>
            <div className="bg-[#0a0a0a] rounded-[10px] p-[8px] shadow-[0_20px_60px_rgba(0,0,0,0.15)]">
              <div className="aspect-[16/10] rounded-[4px] overflow-hidden bg-[#F8F7FA] flex flex-col">
                <div className="bg-[#3F3F3F] px-3 py-2 text-[9px] font-bold text-white">
                  {t("Other platform · Menu Board", "Otra plataforma · Menu Board")}
                </div>
                <div className="flex-1 p-4 grid grid-cols-2 gap-2 content-start">
                  {["Item A · $9", "Item B · $4", "Item C · $7", "Item D · $5"].map((item) => (
                    <div key={item} className="border border-black/10 rounded p-2 bg-white">
                      <div className="text-[10px] font-bold text-black/65">{item}</div>
                    </div>
                  ))}
                </div>
                <div className="bg-[#3F3F3F] px-3 py-2 text-[8px] text-white/55 text-right">
                  {t("Powered by other platform", "Powered by otra plataforma")}
                </div>
              </div>
            </div>
            <p className="mt-4 text-[12px] font-medium text-redy-secondary leading-relaxed text-center">
              {t(
                'Their colors. Their fonts. Their logo at the bottom.',
                'Sus colores. Sus fuentes. Su logo abajo.'
              )}
            </p>
          </motion.div>

          {/* Redy custom — branded */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, delay: 0.25, ease: [0.21, 0.47, 0.32, 0.98] }}
          >
            <div className="text-[10px] font-black tracking-widest uppercase mb-4" style={{ color: "#D7263D" }}>
              {t("Your venue · branded", "Tu venue · branded")}
            </div>
            <div className="bg-[#0a0a0a] rounded-[10px] p-[8px] shadow-[0_30px_80px_rgba(0,0,0,0.2)]">
              <div className="aspect-[16/10] rounded-[4px] overflow-hidden bg-redy-charcoal flex flex-col relative">
                {/* Hero gradient + photo placeholder */}
                <div
                  className="h-[40%] relative overflow-hidden flex items-end p-3"
                  style={{
                    background: "linear-gradient(135deg, #F4722B 0%, #D7263D 60%, #1A1A1A 100%)",
                  }}
                >
                  <div
                    className="absolute inset-0 opacity-40 pointer-events-none"
                    style={{
                      backgroundImage:
                        "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.4) 0%, transparent 50%)",
                    }}
                  />
                  <div className="relative">
                    <div className="text-[7px] font-bold uppercase tracking-widest text-white/85">
                      Dallas · since 2012
                    </div>
                    <div className="text-[16px] font-black text-white leading-none tracking-tight">
                      Pepitos
                    </div>
                    <div className="text-[7px] font-bold uppercase tracking-wider text-white/85 mt-0.5">
                      Birria · Tacos · Aguas Frescas
                    </div>
                  </div>
                </div>

                {/* Menu items */}
                <div className="flex-1 p-3 grid grid-cols-2 gap-2 content-start bg-redy-charcoal text-white">
                  {[
                    { name: "Birria Plate", price: "$12" },
                    { name: "Quesabirria", price: "$14" },
                    { name: "Carnitas Taco", price: "$4" },
                    { name: "Agua Fresca", price: "$4" },
                  ].map((item) => (
                    <div
                      key={item.name}
                      className="bg-white/[0.06] border border-white/10 rounded-[4px] p-2"
                    >
                      <div className="text-[8px] font-bold text-white truncate">{item.name}</div>
                      <div
                        className="text-[12px] font-black tabular-nums"
                        style={{
                          background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
                          WebkitBackgroundClip: "text",
                          backgroundClip: "text",
                          color: "transparent",
                        }}
                      >
                        {item.price}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <p className="mt-4 text-[12px] font-medium text-redy-charcoal leading-relaxed text-center">
              {t(
                'Your photo. Your logo. Your colors. Your restaurant.',
                'Tu foto. Tu logo. Tus colores. Tu restaurante.'
              )}
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
